import { useEffect, useRef, useState } from 'react';
import { userAppContext } from '@/context/Context';
import StickyNote from './StickyNote';
import { STICKY_NOTE_COLORS } from './types';
import { useStickyNotesStorage } from './useStickyNotesStorage';
import './StickyNotes.css';

const COLOR_LABELS: Record<(typeof STICKY_NOTE_COLORS)[number], string> = {
  yellow: 'Yellow',
  green: 'Green',
  orange: 'Orange',
  red: 'Red',
  blue: 'Blue',
  pink: 'Pink',
};

function notePreview(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  if (!trimmed) return 'Empty note';
  return trimmed.length > 36 ? `${trimmed.slice(0, 36)}…` : trimmed;
}

export default function StickyNotesWidget() {
  const context = userAppContext();
  const user = context?.user ?? null;
  const {
    openNotes,
    minimizedNotes,
    addNote,
    updateNote,
    bringToFront,
    restoreNote,
    removeNote,
    canAddMore,
  } = useStickyNotesStorage(user);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!paletteOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPaletteOpen(false);
    };

    const onPointerDown = (event: MouseEvent | PointerEvent) => {
      const target = event.target as Node;
      if (paletteRef.current?.contains(target) || toggleRef.current?.contains(target)) {
        return;
      }
      setPaletteOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [paletteOpen]);

  const handlePickColor = (color: (typeof STICKY_NOTE_COLORS)[number]) => {
    if (!canAddMore) return;
    addNote(color);
    setPaletteOpen(false);
  };

  const handleRestore = (id: string) => {
    restoreNote(id);
    setPaletteOpen(false);
  };

  return (
    <>
      {openNotes.map(note => (
        <StickyNote
          key={note.id}
          note={note}
          onUpdate={updateNote}
          onBringToFront={bringToFront}
          onRemove={removeNote}
        />
      ))}

      <div className="sticky-notes-fab-wrap">
        {paletteOpen && (
          <div ref={paletteRef} className="sticky-notes-palette" role="menu" aria-label="Sticky notes menu">
            <p className="sticky-notes-palette__privacy">
              Notes are saved only on this device (browser local storage). They are not stored in DISGENET
              databases.
            </p>

            <p className="sticky-notes-palette__section-title">New note</p>
            {!canAddMore && <p className="sticky-notes-palette__limit">Maximum notes reached</p>}
            <div className="sticky-notes-palette__colors">
              {STICKY_NOTE_COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  role="menuitem"
                  className={`sticky-notes-palette__swatch sticky-notes-palette__swatch--${color}`}
                  onClick={() => handlePickColor(color)}
                  disabled={!canAddMore}
                  aria-label={`Add ${COLOR_LABELS[color]} sticky note`}
                />
              ))}
            </div>

            {minimizedNotes.length > 0 && (
              <div className="sticky-notes-palette__saved">
                <p className="sticky-notes-palette__section-title">Saved notes</p>
                <ul className="sticky-notes-palette__list">
                  {minimizedNotes.map(note => (
                    <li key={note.id} className="sticky-notes-palette__item">
                      <button
                        type="button"
                        className="sticky-notes-palette__restore"
                        onClick={() => handleRestore(note.id)}
                        aria-label={`Restore note: ${notePreview(note.text)}`}
                      >
                        <span
                          className={`sticky-notes-palette__dot sticky-notes-palette__swatch--${note.color}`}
                          aria-hidden
                        />
                        <span className="sticky-notes-palette__preview">{notePreview(note.text)}</span>
                      </button>
                      <button
                        type="button"
                        className="sticky-notes-palette__delete-saved"
                        onClick={() => removeNote(note.id)}
                        aria-label={`Delete saved note: ${notePreview(note.text)}`}
                        title="Delete"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <button
          ref={toggleRef}
          type="button"
          className={`sticky-notes-toggle ${paletteOpen ? 'sticky-notes-toggle--open' : ''}`}
          onClick={() => setPaletteOpen(open => !open)}
          aria-label="Sticky notes"
          aria-expanded={paletteOpen}
          aria-haspopup="menu"
        >
          <i className="bi bi-sticky-fill" aria-hidden style={{ fontSize: '17px' }} />
        </button>
      </div>
    </>
  );
}
